import { Card, useTheme } from "@mui/material";
import DescriptionIcon from '@mui/icons-material/Description';
import LoopIcon from '@mui/icons-material/Loop';
import { MedicalWasteItem, WasteStatus } from "../types";
import CompliancePlip from "../../../../components/CompliancePlip";
import { sharpsIcon } from "../../../../icons";
import { standardDateFormat } from "../../../../helpers/date";
import { TableButton } from "../../../../components/Buttons.styled";
import { useState } from "react";
import axios from "axios";
import constants from "../../../../constants";
import ManifestModal from "../components/ManifestModal";
import { useAppDispatch } from "../../../../redux/store";
import trilogySignature from "../../../../images/signature_trilogy.png";
import mwsSignature from "../../../../images/signature_mws.png";
import ActiveInactiveModal from "../components/ActiveInactiveModal";
import { itemImages } from "../../../../helpers/image";
import { addError, addSuccess } from "../../../../redux/alerts";
import useCustomer from "../../../../hooks/useCustomer";

type Props = {
  mwaste: MedicalWasteItem,
  getWasteData: () => Promise<void>,
}

const WasteItem: React.FC<Props> = ({ mwaste, getWasteData }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const customer = useCustomer()
  const itemStatus = mwaste.used_date === null ? 'inUse' : 'used';

  const [loadingItemId, setLoadingItemId] = useState<number | null>(null);
  const [openManifest, setOpenManifest] = useState<boolean>(false);
  const [openActiveUsed, setOpenActiveUsed] = useState<boolean>(false);

  const vendors = {
    mws: {
      name: 'Medical Waste Services',
      street: '7321 Quimby Street',
      city: 'Paramount',
      state: 'CA',
      zip: '90723',
      facilityPermit: 'OST/TS #94',
      processingRep: 'Mario Arellano',
      signatureImage: mwsSignature
    },
    trilogy: {
      name: 'Trilogy Medwaste Southeast, LLC',
      street: '148 Boxwood Lane',
      city: 'Gastonia',
      state: 'NC',
      zip: '28054',
      facilityPermit: '3615-TP-2018',
      processingRep: 'Jerry Norris',
      signatureImage: trilogySignature
    }
  }

  const processor = mwaste.vendor?.toLowerCase() === 'mws'
    ? vendors.mws
    : vendors.trilogy

  const getStatus = (status: number) => {
    if (status === WasteStatus.inUse) {
      return 'In Use';
    } else if (status === WasteStatus.used) {
      return 'Used';
    } else if (status === WasteStatus.destroyed) {
      return 'Processed';
    }
    return 'Unknown'
  }

  const getImage = (image: string | undefined) => {
    if (image) {
      const itemImage = itemImages[`${image}.png`];
      return itemImage
    }
    return sharpsIcon;
  }

  const handleMarkAsUsedActive = async (usedDate?: string | null) => {
    setOpenActiveUsed(false);

    try {
      const itemDetails = {
        disposal_status: mwaste.disposal_status === WasteStatus.inUse ? WasteStatus.used : WasteStatus.inUse,
        used_date: mwaste.disposal_status === WasteStatus.used ? null : usedDate ?? String(new Date())
      }

      setLoadingItemId(mwaste.mwaste_id)

      await axios.put(`${constants.ONTRAQ_API_URL}/api2/${mwaste.customer_number}/mwastes/${mwaste.mwaste_id}`, { ...itemDetails })
      await getWasteData();

      dispatch(addSuccess(mwaste.disposal_status === WasteStatus.used ? 'The item has been marked as In Use' : 'The item has been marked as Used'));
      setLoadingItemId(null);
    } catch (error) {
      setLoadingItemId(null);
      console.error(error);
      dispatch(addError(error instanceof Error ? error.message : 'There was a problem updating the item'));
    }
  }

  const handleClose = () => {
    setOpenManifest(false);
  }

  const handleOpen = () => {
    setOpenManifest(true);
  }

  return (
    <Card sx={{ p: 2, mb: 2, color: theme.palette.color50555b.main, display: 'flex', flex: 'space-between' }} variant="outlined">
      <div className="flex flex-align-start flex-1">
        <img src={getImage(mwaste.item?.images)} alt="kit" style={{ width: '50px' }} className="m-r-12" />
        <div>
          <div className="flex flex-align-center">
            <CompliancePlip medium={true} status={mwaste.status} message={mwaste.mwaste_compliance?.compliance?.message_short} className="m-r-12" />
            <span className="f-s-17 m-r-8">{mwaste.item?.name ?? 'Sharps & Medical Waste Container'}</span>
            <LoopIcon sx={{ color: mwaste.isRenewal ? theme.palette.success.main : theme.palette.inactive.main }} fontSize="small" />
          </div>
          <div className="f-s-13">
            <div>Items #: {mwaste.item_number}</div>
            <div>Product Serial #: {mwaste.serial_number}</div>
            {mwaste.used_date && <div>Used: {standardDateFormat(mwaste.used_date)}</div>}
            {mwaste.received_date && <div>Shipped: {standardDateFormat(mwaste.received_date)}</div>}
            {mwaste.received_date && <div>Received: {standardDateFormat(mwaste.received_date)}</div>}
            {mwaste.destroyed_date && <div>Processed: {standardDateFormat(mwaste.destroyed_date)}</div>}
            <div>Status: {getStatus(mwaste.disposal_status)}</div>
            {mwaste.disposal_status === WasteStatus.destroyed && (
              <div className="flex link pointer" onClick={handleOpen}>
                <DescriptionIcon fontSize="small" />
                <div>Processing Certificate</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {(mwaste.disposal_status === WasteStatus.inUse || mwaste.disposal_status === WasteStatus.used) && (
        <div>
          <TableButton variant="contained" onClick={() => setOpenActiveUsed(true)} disabled={loadingItemId === mwaste.mwaste_id}>
            Mark as {mwaste.disposal_status === WasteStatus.inUse ? 'Used' : 'Active'}
          </TableButton>
        </div>
      )}
      <ManifestModal open={openManifest} handleClose={handleClose} category="medicalWaste" customer={customer} item={mwaste} processor={processor} />
      <ActiveInactiveModal itemStatus={itemStatus} open={openActiveUsed} handleClose={() => setOpenActiveUsed(false)} handleSubmit={handleMarkAsUsedActive} />
    </Card>
  )
}

export default WasteItem;